import React, { useContext, useEffect, useRef, useState } from "react";
import {
  requestForAccountDetails,
} from "../../api";
import AccountDetails from "../AccountDetails/AccountDetails";
import { NetworkErrorAlertContext } from "../../../../context/NetworkErrorAlert";
import { SuccessAlertContext } from "../../../../context/SuccessAlertContext";
import { Content, SlideDown } from "../AccountInfo/style";
import { useParams } from "react-router";
import NGNVirutalAccount from "../VirtualAccounts/NGNVirutalAccount";
import { useAppDispatch, useAppSelector } from "../../../../app/hooks";
import { Account } from "../../../../types/walletTypes";
import {capitalizeCurrencyCode} from "../../../../utils/helpers";
import { CURRENCIES_ALLOWED_FOR_VA } from "../../utils";
import LoadingVirtualAccount from "./LoadingInterface";
import { allVirtualAccounts } from "../../redux/slice";

interface Props {
  onDone?: () => void;
  account?: Account;
  currency?: string;
  onShowAlert?: any;
  onShowSuccessAlert?: any;
  isVisible?: boolean;
}

const ReceiveFunds = ({ onDone, isVisible }: Props) => {
  const { businessId } = useAppSelector((state) => state.users);
  const dispatch = useAppDispatch()

  const { business } = useAppSelector((state) => state?.settings);
  const [open, setOpen] = useState(true);
  const [activeKey, setActiveKey] = useState("1");
  const [loading, setLoading] = useState(false);
  const [virtualAccountLoading, setVirtualAccountLoading] = useState(false);
  const { onShowAlert: onShowSuccessAlert } = useContext(SuccessAlertContext);
  const [showBankDetail, setShowBankDetail] = useState(false);
  const [virtualAccount, setVirtualAccount] = useState([]);
  const [loadingHeader, setLoadingHeader] = useState("Loading...");
  const [loadingSubHeader, setLoadingSubHeader] = useState("We are fetching your virtual account.");

  const { onShowAlert } = useContext(NetworkErrorAlertContext);
  const { currencyCode } = useParams();
  const resolvedVirtualAccount = virtualAccount.find(
    (data) => capitalizeCurrencyCode(data.currency) === capitalizeCurrencyCode(currencyCode)
  );

  const resolvedIntVirtualAccount = virtualAccount.find(
    (data) => capitalizeCurrencyCode(data.currency) !== "NGN" && capitalizeCurrencyCode(data.currency) === capitalizeCurrencyCode(currencyCode) && CURRENCIES_ALLOWED_FOR_VA.includes(capitalizeCurrencyCode(currencyCode))
  );

  const handlePanelChange = () => {
    setOpen(!open);
  };

  const pollTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

const fetchVirtualAccount = async () => {
  if (pollTimeoutRef.current) {
    clearTimeout(pollTimeoutRef.current);
    pollTimeoutRef.current = null;
  }

  setLoading(true);
  setLoadingHeader("Loading...");
  setLoadingSubHeader("We are fetching your virtual account.");

  try {
    const action = await dispatch(allVirtualAccounts({ businessId }));

    if (allVirtualAccounts.rejected.match(action)) {
      onShowAlert(action.error, action.error.message || "Failed to fetch virtual accounts.");
      setLoading(false);
      return;
    }

    if (allVirtualAccounts.fulfilled.match(action)) {
      const data = action.payload;
      const hasCurrencyAccount = Array.isArray(data) && data.some(
        (acc) => capitalizeCurrencyCode(acc.currency) === capitalizeCurrencyCode(currencyCode)
      );
      if (hasCurrencyAccount) {
        setVirtualAccount(data);
        setShowBankDetail(true);
        setLoading(false);
        return;
      }
    }

    if (currencyCode === 'ngn') { 
      setLoading(false);
      return;
    }

    setLoadingHeader(`Keep this modal open...`);
    setLoadingSubHeader(`Still fetching ${capitalizeCurrencyCode(currencyCode)} top-up account. This can take up to a minute. We'll return your account details soon.`);

    let attempts = 0;
    const maxAttempts = 24;

    const poll = async () => {
      attempts += 1;
      try {
        const action = await dispatch(allVirtualAccounts({ businessId }));

        if (allVirtualAccounts.rejected.match(action)) {
          setLoading(false);
          setShowBankDetail(false);
          onShowAlert(action.error, action.error.message || "Failed to fetch virtual account.");
          return; 
        }

        if (allVirtualAccounts.fulfilled.match(action)) {
          const polledData = action.payload;

          const hasPolledCurrencyAccount = Array.isArray(polledData) && polledData.some(
            (acc) => capitalizeCurrencyCode(acc.currency) === capitalizeCurrencyCode(currencyCode)
          );

          if (hasPolledCurrencyAccount) {
            setVirtualAccount(polledData);
            setShowBankDetail(true);
            setLoading(false);
            return; 
          } else if (attempts >= maxAttempts) {
            setLoadingHeader("Account fetching took too long.");
            setLoadingSubHeader(`We were unable to retrieve your newly created ${capitalizeCurrencyCode(currencyCode)} top-up account right now. Please try again later or contact our support team for assistance.`);
            setShowBankDetail(false);
            return; 
          }
        }
      } catch (error) {
        onShowAlert(error, error.message);
        setLoading(false);
        setShowBankDetail(false);
        return;
      }

      pollTimeoutRef.current = setTimeout(poll, 5000);
    };

    pollTimeoutRef.current = setTimeout(poll, 5000);

  } catch (error) {
    onShowAlert(error, error.message);
    setShowBankDetail(false);
    setLoading(false);
    setActiveKey(null);
  }
};


  useEffect(() => {
    if (businessId && isVisible) {
      fetchVirtualAccount();
    }
    return () => {
      if (pollTimeoutRef.current) {
        clearTimeout(pollTimeoutRef.current);
        pollTimeoutRef.current = null;
      }
    };
  }, [businessId, isVisible]);


  const handleRequestAccountDetails = async (data) => {
    setVirtualAccountLoading(true);
    const commonPayload = {
      businessId,
      bvn: data.bvn,
    };

    const ngnPayload = {
      ...commonPayload,
    };

    const internationalPayload = {
      ...commonPayload,
      paymentFlowDescription: data.paymentFlowDescription,
      monthlyVolume: data.monthlyVolume,
      reason: data.reason,
      KYCInformation: {
        city: String(business.city),
        state: String(business.state),
        zip: data.zip,
      },
    };

    try {
      let payload = {};
      if (currencyCode === "ngn") {
        payload = ngnPayload;
      } else {
        payload = internationalPayload;
      }

      await requestForAccountDetails(payload);
      setVirtualAccountLoading(false);
      await fetchVirtualAccount();
      onShowSuccessAlert("Account requested");
    } catch (error) {
      setVirtualAccountLoading(false);
      onShowAlert(error, error);
    }
  };

  return (
    <Content
      open={open}
      onChange={handlePanelChange}
      defaultActiveKey={activeKey}
    >
      <SlideDown currencyCode={capitalizeCurrencyCode(currencyCode)} header={""} key="1">
        {loading ? (
          <LoadingVirtualAccount heading={loadingHeader} subHeading={loadingSubHeader} />
        ) : (
          <div>
            {currencyCode === "ngn" && (
              <div>
                {resolvedVirtualAccount ? (
                  <AccountDetails
                    bankName={resolvedVirtualAccount?.bankName}
                    accountName={resolvedVirtualAccount?.accountName}
                    accountNumber={resolvedVirtualAccount?.accountNumber}
                    accountType={resolvedVirtualAccount?.accountType}
                  />
                ) : (
                  <NGNVirutalAccount
                    onSubmit={handleRequestAccountDetails}
                    openModal={open}
                    currencyCode={currencyCode}
                    showBankDetail={showBankDetail}
                    loading={virtualAccountLoading}
                  />
                )}
              </div>
            )}

            {currencyCode !== "ngn" && (
              <div>
                {resolvedIntVirtualAccount && (
                  <AccountDetails
                    bankName={resolvedIntVirtualAccount?.bankName}
                    accountName={resolvedIntVirtualAccount?.accountName}
                    accountNumber={resolvedIntVirtualAccount?.accountNumber}
                    accountType={resolvedIntVirtualAccount?.accountType}
                    callBack={onDone}
                  />
                )}
              </div>
            )}
          </div>
        )}
      </SlideDown>
    </Content>
  );
};

export default ReceiveFunds;
